/* HC custom — collection tabs switcher. The section's blocks render panels
   (each carrying data-tab-label); this element builds the tab bar from them,
   then runs the standard tablist keyboard contract: click or Enter/Space
   activates, Left/Right/Home/End move and activate, roving tabindex keeps
   one tab stop for the list. Panels just toggle [hidden], so switching is
   instant. Selecting a tab block in the theme editor activates its tab. */

class HcCollectionTabs extends HTMLElement {
  connectedCallback() {
    this.#build();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    /* HC: window held #syncArrows, which closes over this element - every
       removal (section morph, editor reload) leaked the whole detached
       subtree. Reset the wired flag so a reconnected instance re-binds. */
    window.removeEventListener('resize', this.#syncArrows);
    this.#arrowsWired = false;
  }

  /* HC page speed: with the section's "Load product photos after the page's first
     image" setting on (default), the cards' photos start lazy (snippets/card-gallery)
     so they don't compete with the hero image. Once the page has loaded, every tab's
     card photos are fetched - the same photos as before, just after the hero - so a
     tab switch shows them at once. Hover photos stay lazy, as they always were. */
  #warmPhotos() {
    const warm = () => {
      this.querySelectorAll(
        '.hc-collection-tabs__panel img.product-media__image:not(.product-media__hover-image)[loading="lazy"]'
      ).forEach((img) => {
        img.loading = 'eager';
      });
    };
    const soon = () => (window.requestIdleCallback ? requestIdleCallback(warm, { timeout: 2000 }) : setTimeout(warm, 200));
    if (document.readyState === 'complete') soon();
    else window.addEventListener('load', soon, { once: true });
  }

  get #tablist() {
    return this.querySelector('[role="tablist"]');
  }

  get #tabs() {
    return [...this.querySelectorAll('[role="tab"]')];
  }

  get #panels() {
    return [...this.querySelectorAll('[role="tabpanel"]')];
  }

  #build() {
    const tablist = this.#tablist;
    const panels = this.#panels;
    if (!tablist || !panels.length) return;

    const prefix = tablist.dataset.tabsPrefix || 'hc-ct';
    tablist.textContent = '';
    panels.forEach((panel, i) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'hc-collection-tabs__tab';
      tab.setAttribute('role', 'tab');
      tab.id = `${prefix}-tab-${i}`;
      panel.id = panel.id || `${prefix}-panel-${i}`;
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('aria-labelledby', tab.id);
      tab.textContent = panel.dataset.tabLabel || `Tab ${i + 1}`;
      tablist.appendChild(tab);
    });
    this.#wireArrows();
    this.#activate(0);
    this.#warmPhotos();
    this.setAttribute('data-ready', '');
  }

  get #activeRail() {
    return this.querySelector('[role="tabpanel"]:not([hidden]) .hc-collection-tabs__rail');
  }

  #wireArrows() {
    const prev = this.querySelector('[data-hc-ct-prev]');
    const next = this.querySelector('[data-hc-ct-next]');
    if (!prev || !next || this.#arrowsWired) return;
    this.#arrowsWired = true;

    const page = (direction) => {
      const rail = this.#activeRail;
      if (!rail) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      rail.scrollBy({ left: direction * rail.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    };
    // aria-disabled (not disabled) so a focused arrow keeps focus at the ends.
    prev.addEventListener('click', () => prev.getAttribute('aria-disabled') !== 'true' && page(-1));
    next.addEventListener('click', () => next.getAttribute('aria-disabled') !== 'true' && page(1));

    for (const rail of this.querySelectorAll('.hc-collection-tabs__rail')) {
      rail.addEventListener('scroll', this.#syncArrows, { passive: true });
    }
    window.addEventListener('resize', this.#syncArrows);
  }

  #arrowsWired = false;

  #syncArrows = () => {
    const prev = this.querySelector('[data-hc-ct-prev]');
    const next = this.querySelector('[data-hc-ct-next]');
    const rail = this.#activeRail;
    if (!prev || !next) return;
    if (!rail) {
      this.removeAttribute('data-overflowing');
      return;
    }
    const overflowing = rail.scrollWidth > rail.clientWidth + 1;
    this.toggleAttribute('data-overflowing', overflowing);
    prev.setAttribute('aria-disabled', String(rail.scrollLeft <= 1));
    next.setAttribute('aria-disabled', String(rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1));
  };

  /** @param {number} index */
  #activate(index) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (panels[i]) panels[i].hidden = !selected;
    });
    this.#syncArrows();
  }

  #handleClick = (/** @type {MouseEvent} */ event) => {
    const tab = /** @type {HTMLElement} */ (event.target).closest?.('[role="tab"]');
    if (!tab || !this.contains(tab)) return;
    this.#activate(this.#tabs.indexOf(tab));
  };

  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    const tab = /** @type {HTMLElement} */ (event.target).closest?.('[role="tab"]');
    if (!tab || !this.contains(tab)) return;
    const tabs = this.#tabs;
    const current = tabs.indexOf(tab);
    let next = null;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    this.#activate(next);
    /** @type {HTMLElement} */ (tabs[next])?.focus();
  };

  #handleBlockSelect = (/** @type {Event & { target: HTMLElement }} */ event) => {
    const panel = event.target?.closest?.('[role="tabpanel"]');
    if (!panel || !this.contains(panel)) return;
    this.#activate(this.#panels.indexOf(panel));
  };

  // Editor re-renders replace children - rebuild the derived tab bar.
  #handleSectionLoad = (/** @type {Event & { target: HTMLElement }} */ event) => {
    if (event.target?.contains?.(this)) this.#build();
  };
}

if (!customElements.get('hc-collection-tabs')) {
  customElements.define('hc-collection-tabs', HcCollectionTabs);
}
